import { HostListener, Injectable } from '@angular/core';
import * as fabric from 'fabric';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, map, Observable, throwError } from 'rxjs';
import { HTTPService } from 'src/app/services/http.service';

type SelectionEvent = fabric.TEvent<fabric.TPointerEvent> & {
  selected?: fabric.Object[]; // Objects selected in this event
  deselected?: fabric.Object[]; // Objects deselected in this event
};


@Injectable({ providedIn: 'root' })
export class CardEditorService {
  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  isLoading$ = this.isLoadingSubject.asObservable();

  private selectedTextboxBoundsSubject = new BehaviorSubject<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  selectedTextboxBounds$ = this.selectedTextboxBoundsSubject.asObservable();

  private highlightRects: fabric.Rect[] = []; // Track active highlight rectangles
  textboxCount: any;
  fontSelector: string | undefined;
  textColorInput: string | fabric.TFiller | undefined;
  isBold: boolean | undefined;
  isItalic: boolean | undefined;
  isAlignLeft: boolean | undefined;
  isAlignCenter: boolean | undefined;
  isAlignRight: boolean | undefined;
  selectedFontSize: string | undefined;
  selectedColor: string | undefined;
  selectedFont: string | undefined;
  serverStoragePath: string = "./assets";
  private currentScale = 1;
  isMobileView: boolean = false; // Detects if the device is mobile
  showTextEditPopup: boolean = false; // Controls the visibility of the popup
  selectedTextContent: string = ''; // Stores the text of the selected textbox
  selectedTextbox: fabric.Textbox | null = null; // Reference to the selected textbox
  totalHeaderHeight = 0;
  headerIds = ["controls"];
  canvasWidth: any;
  canvasHeight: any;
  newCardBackground: string = "";
  newCardPlaceholder: string | undefined;
  newCardThumbnail: string | undefined;
  newCardHoverTheme: string | undefined;
  marker: any;
  newCardUploaderLeft: any;
  newCardUploaderTop: any;
  newCardWithPhoto: any;
  singleFileList: any = [];
  userDetails: any = {};
  isTextBoxSelected: boolean = true;
  private alignmentGuides: fabric.Line[] = []; // Store alignment guide lines
  private SNAP_THRESHOLD = 5; // Pixel threshold for snapping
  private GUIDE_COLOR = 'rgba(0, 128, 255, 0.5)';
  private GUIDE_STROKE_WIDTH = 1;
  popupTextAlign: string;

  public lineHeights: number[] = [1, 1.2, 1.5, 1.8, 2, 2.5, 3]; // Available line height options
  public selectedLineHeight: number = 1;

  private copiedFormatting: {
    fontFamily?: string;
    fontSize?: number;
    fill?: string;
    fontWeight?: string | number;
    fontStyle?: string;
    textAlign?: string;
    lineHeight?: number;
  } | null = null;

  constructor(private http: HttpClient, private _httpService: HTTPService) { }

  // Load the canvasConfig JSON from assets
  loadCanvasConfig(filePath: string): Observable<any> {
    return this.http.get(filePath);
  }
  loadLanguageElements(event: any) {
    throw new Error('Method not implemented.');
  }
  public canvas!: fabric.Canvas;
  canvasConfig: any;
  uploaderElement: any;


  //   initializeCanvas(canvasId: string): void {
  //     this.canvas = new fabric.Canvas(canvasId, { preserveObjectStacking: true });
  //     this.canvas.hoverCursor = 'move';

  // }


  initializeCanvasWithLoader(canvasId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.showLoader(); // Show the loader
        this.GUIDE_COLOR = this.canvasConfig?.hovertheme || 'Grey';
        // Initialize the fabric canvas
        this.canvas = new fabric.Canvas(canvasId, {
          preserveObjectStacking: true, selection: false, // disabled group selection
          perPixelTargetFind: true
        });
        this.canvas.hoverCursor = 'move';

        // Example of optional logic for ensuring initialization
        setTimeout(() => {
          // If any additional setup is needed, it can be placed here
          //console.log('Canvas initialized with loader');
          this.hideLoader(); // Hide the loader once initialization is complete
          resolve(); // Resolve the promise to indicate completion
        }, 1000); // Add delay if rendering takes time (adjust as necessary)
      } catch (error) {
        console.error('Error initializing the canvas:', error);
        reject(error); // Reject the promise on error
      }
    });
  }



  copyTextboxFormatting(): void {
    if (!this.canvas) {
      console.error('Canvas is not initialized');
      return;
    }
  
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject || activeObject.type !== 'textbox') {
      console.warn('No textbox selected for copying formatting');
      return;
    }
  
    const textbox = activeObject as fabric.Textbox;
    this.copiedFormatting = {
      fontFamily: textbox.fontFamily || 'Arial',
      fontSize: textbox.fontSize || 16,
      fill: textbox.fill?.toString() || '#000000',
      fontWeight: textbox.fontWeight || 'normal',
      fontStyle: textbox.fontStyle || 'normal',
      textAlign: textbox.textAlign || 'left',
      lineHeight: textbox.lineHeight || 1,
    };
  
    console.log('Formatting copied:', this.copiedFormatting);
  }
  
  
  pasteTextboxFormatting(): void {
    if (!this.canvas) {
      console.error('Canvas is not initialized');
      return;
    }
  
    if (!this.copiedFormatting) {
      console.warn('No formatting available to paste');
      return;
    }
  
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject || activeObject.type !== 'textbox') {
      console.warn('No textbox selected to apply formatting');
      return;
    }
  
    const textbox = activeObject as fabric.Textbox;
    textbox.set({
      fontFamily: this.copiedFormatting.fontFamily,
      fontSize: this.copiedFormatting.fontSize,
      fill: this.copiedFormatting.fill,
      fontWeight: this.copiedFormatting.fontWeight,
      fontStyle: this.copiedFormatting.fontStyle,
      textAlign: this.copiedFormatting.textAlign,
      lineHeight: this.copiedFormatting.lineHeight,
    });
  
    // Update controls to reflect the new formatting
    this.updateControls(textbox);
  
    // Fire selection event to sync UI
    this.canvas.fire('selection:updated', {
      selected: [textbox as fabric.Object], // Cast to fabric.Object for type compatibility
      deselected: [], // Empty array since no objects were deselected
    });
  
  
    // Render canvas
    this.canvas.renderAll();
  
    
  }

  duplicateTextbox(): fabric.Textbox | null {
    if (!this.canvas) {
      console.error('Canvas is not initialized');
      return null;
    }
  
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject || activeObject.type !== 'textbox') {
      console.warn('No textbox selected to duplicate');
      return null;
    }
  
    const textbox = activeObject as fabric.Textbox;
  
    // Clone the textbox with explicit properties
    const clonedTextbox = new fabric.Textbox(textbox.text || 'Duplicated Text', {
      left: (textbox.left || 0) + 20, // Offset to avoid overlap
      top: (textbox.top || 0) + 20,
      scaleX: textbox.scaleX ?? 1,
      scaleY: textbox.scaleY ?? 1,
      fontSize: textbox.fontSize || 16,
      fontFamily: textbox.fontFamily || 'Arial',
      fill: textbox.fill || '#000000',
      width: textbox.width || 200,
      angle: textbox.angle || 0,
      textAlign: textbox.textAlign || 'left',
      fontWeight: textbox.fontWeight || 'normal',
      fontStyle: textbox.fontStyle || 'normal',
      underline: textbox.underline || false,
      lineHeight: textbox.lineHeight ?? 1,
      selectable: true, // Ensure the cloned textbox is selectable
      id: `textbox_${this.textboxCount++}`, // Unique ID
    });
  
    // Add cloned textbox to canvas
    this.canvas.add(clonedTextbox);
    this.canvas.setActiveObject(clonedTextbox);
    this.canvas.renderAll();
  
    // Update controls and selected textbox
    this.selectedTextbox = clonedTextbox;
    this.updateControls(clonedTextbox);
  
    // Fire selection:updated to sync UI
    this.canvas.fire('selection:updated', {
      selected: [clonedTextbox as fabric.Object],
      deselected: [textbox as fabric.Object],
    });
  
    
    return clonedTextbox;
  }





  getActiveObject(): fabric.Object | null {
    return this.canvas ? this.canvas.getActiveObject() : null;
  }

  // changed on 12.04.2025 for hiding non selected object highlights when moving on canvas
  // addHighlightRects(): void {
  //   if (!this.canvas) {
  //     console.error('Canvas not initialized');
  //     return;
  //   }

  //   const hoverTheme = this.canvasConfig?.hovertheme || 'Grey';
  //   if (!this.canvas.getActiveObject()) {
  //     this.canvas.getObjects().forEach((obj) => {
  //       if ((obj.type === 'textbox' || obj.type === 'image') && obj !== this.canvas!.backgroundImage) {
  //         const bounds = obj.getBoundingRect();

  //         const highlightRect = new fabric.Rect({
  //           left: bounds.left,
  //           top: bounds.top,
  //           width: bounds.width,
  //           height: bounds.height,
  //           fill: 'rgba(255,255,255,0.0)', // Transparent fill
  //           stroke: hoverTheme, // Border color
  //           strokeDashArray: [4, 4], // Dashed border
  //           strokeWidth: 0.3,
  //           selectable: false, // Non-selectable
  //           evented: false, // Non-interactive
  //           zIndex: -1, // Behind other objects
  //         });

  //         this.highlightRects.push(highlightRect);
  //         this.canvas?.add(highlightRect);
  //       }
  //     });

  //     this.canvas.renderAll();
  //   }
  // }

  addHighlightRects(): void {
    if (!this.canvas) {
      console.error('Canvas not initialized');
      return;
    }

    // Remove existing highlights to start fresh
    this.removeHighlightRects();
    // console.log('Adding highlight rectangles...');

    const hoverTheme = this.canvasConfig?.hovertheme || 'Grey';
    const activeObject = this.canvas.getActiveObject();
    const padding = 5;

    this.canvas.getObjects().forEach((obj) => {
      // Only add highlights for textbox or image, excluding background image and active object
      if (
        (obj.type === 'textbox' || (obj.type === 'image' && !(obj as any).Background)) &&
        obj !== activeObject
      ) {
        const bounds = obj.getBoundingRect();
        
        const isTextbox = obj.type === 'textbox';
        const paddingX = isTextbox ? padding : 0; // Horizontal padding
        const paddingY = isTextbox ? padding : 0; // Vertical padding

        const highlightRect = new fabric.Rect({
          left: bounds.left - paddingX,
          top: bounds.top - paddingY,
          width: bounds.width + 2 * paddingX,
          height: bounds.height + 2 * paddingY,
          fill: 'rgba(255,255,255,0.0)', // Transparent fill
          stroke: hoverTheme, // Border color
          strokeDashArray: [5, 3], // Dashed border
          strokeWidth: 0.4,
          selectable: false,
          evented: false,
          zIndex: -1,
        });

        // Store reference to the associated object for easier management
        (highlightRect as any).associatedObject = obj;
        this.highlightRects.push(highlightRect);
        this.canvas.add(highlightRect);
        // console.log(`Added highlight for object: ${obj.type}, ID: ${(obj as any).id || 'no-id'}`);
      }
    });

    // console.log(`Total highlights added: ${this.highlightRects.length}`);
    this.canvas.renderAll();
  }

  // Remove highlight rectangles
  removeHighlightRects(): void {
    if (!this.canvas) {
      console.error('Canvas not initialized');
      return;
    }

    this.highlightRects.forEach((rect) => this.canvas!.remove(rect));
    this.highlightRects = [];
    this.canvas.renderAll();
  }


  // // Handle mouse:move
  //  handleMouseMove(event: fabric.TEvent): void {
  //   if (!this.canvas) return;

  //   const pointer = this.canvas.getPointer(event.e); // Mouse position
  //   const hoveredObject = this.canvas.findTarget(event.e); // Object under mouse

  //   if (!hoveredObject) {
  //     this.addHighlightRects(); // Add highlight rectangles if no object is hovered
  //   }
  // }

  // // Handle mouse:out
  // handleMouseOut(event: fabric.TEvent): void {
  //   if (!this.canvas) return;

  //   this.removeHighlightRects(); // Remove highlights

  //   const overlay = document.getElementById('image-overlay');

  //   this.canvas.setCursor('default'); // Reset cursor to default
  // }
  handleMouseOut(event: fabric.TPointerEvent): void {
    if (!this.canvas) return;
    // console.log('Mouse Out Of Canvas');
    const target = event.target as unknown as fabric.Object | null;

    if (target instanceof fabric.Textbox) {
      // 🔹 Reset textbox background when the mouse leaves
      target.set({
        backgroundColor: '',
      });


      this.canvas.setCursor('default'); // Reset cursor
      this.canvas.renderAll();
    }
    if (target == undefined) {
      this.removeHighlightRects();
    }
  }



  handleSelectionCreated(event: SelectionEvent): void {
    const target = event.selected?.[0]; // Get the first selected object
    if (!this.canvas || !target) return;

    //console.log('Selection Created:', target.type);

    if (target.type === 'textbox' || target.type === 'image') {
      target.set({
        hasControls: true,
        borderColor: this.canvasConfig?.hovertheme || 'blue',
        //cornerColor: this.canvasConfig?.hovertheme || 'blue',
        cornerSize: 10,
        borderScaleFactor: 1.5,
        transparentCorners: false,
        cornerColor: '#ffffff', // White corners
        cornerStrokeColor: this.canvasConfig?.hovertheme || 'blue', // Border around corners
        cornerStyle: 'circle', // Circular corners
      });

      const textControls = document.getElementById('text-controls');
      const textControlsMobile = document.getElementById('mobile-controls');

      this.isTextBoxSelected = target.type === 'textbox';


      if (textControls) {
        textControls.classList.add('active');
      } else {
        console.warn("Text controls element not found");
      }

      // Update controls for textboxes

      if (target.type === 'textbox') {

        this.updateControls(target as fabric.Textbox); // Call updateControls directly

        this.selectedTextbox = target as fabric.Textbox;
        this.selectedTextContent = this.selectedTextbox.text || '';
        // Open popup in mobile view

        console.log('this.selectedTextbox.left',+this.selectedTextbox.left);
      console.log('this.selectedTextbox.top',+this.selectedTextbox.top);
      
        this.selectedTextboxBoundsSubject.next({
          left: this.selectedTextbox.left || 0,
          top: this.selectedTextbox.top || 0,
          width: (this.selectedTextbox.width || 200) * (this.selectedTextbox.scaleX || 1),
          height: (this.selectedTextbox.height || 50) * (this.selectedTextbox.scaleY || 1),
        });
      }
      if (this.isMobileView && textControlsMobile) {
        // console.log("Mobile view and textcontrols");
        textControlsMobile.style.display = 'block';
        requestAnimationFrame(() => {
          textControlsMobile.classList.add('active');
        });
      }
      //this.removeHighlightRects(); ---keep borders on even when the textbox is selected.
      this.addHighlightRects();
      this.canvas.renderAll();
    }
  }

  applyTextChanges(): void {
    if (this.selectedTextbox && this.canvas) {
      this.selectedTextbox.set('text', this.selectedTextContent); // Update textbox text
      this.canvas.renderAll(); // Re-render the canvas
      this.closeTextEditPopup(); // Close the popup
    }
  }

  closeTextEditPopup(): void {
    // console.log('On close popup, Rest selected textbox properties ');
    this.showTextEditPopup = false; // Hide the popup
    this.selectedTextContent = '';
    this.popupTextAlign = 'left';
    //this.selectedTextbox = null; // Reset the selected textbox
  }

  updateMobileViewStatus(isMobileView: boolean): void {
    //console.log('Mobile View--??' + isMobileView);
    this.isMobileView = isMobileView;
    if (this.isMobileView) {
      const textControls = document.getElementById('controls');
      if (textControls) {
        textControls.style.display = 'none';
      } else {
        console.warn("Text controls element not found in the DOM");
      }
    }

  }



  handleSelectionUpdated(event: SelectionEvent): void {
    const target = event.selected?.[0];
    if (!this.canvas || !target) return;

    const textControlsMobile = document.getElementById('mobile-controls');
    //console.log('Selection Updated:', target.type);

    if (target.type === 'textbox' || target.type === 'image') {
      target.set({
        hasControls: true,
        borderColor: this.canvasConfig?.hovertheme || 'blue',
        //cornerColor: this.canvasConfig?.hovertheme || 'blue',
        cornerSize: 10,
        borderScaleFactor: 1.5,
        transparentCorners: false,
        cornerColor: '#ffffff', // White corners
        cornerStrokeColor: this.canvasConfig?.hovertheme || 'blue', // Border around corners
        cornerStyle: 'circle', // Circular corners
      });

      this.isTextBoxSelected = target.type === 'textbox';
      this.selectedTextbox = target as fabric.Textbox;
      this.selectedTextContent = this.selectedTextbox.text || '';
      // Open popup in mobile view
      //console.log("checking if this is mobile view-->" + this.isMobileView);

      this.selectedTextboxBoundsSubject.next({
        left: this.selectedTextbox.left || 0,
        top: this.selectedTextbox.top || 0,
        width: (this.selectedTextbox.width || 200) * (this.selectedTextbox.scaleX || 1),
        height: (this.selectedTextbox.height || 50) * (this.selectedTextbox.scaleY || 1),
      });

      this.updateControls(target as fabric.Textbox);

      if (this.isMobileView && textControlsMobile) {
        textControlsMobile.style.display = 'block';
        requestAnimationFrame(() => {
          textControlsMobile.classList.add('active');
        });
      }
      //this.updateControls(target);
      //this.removeHighlightRects();
      this.addHighlightRects();
      this.canvas.renderAll();
    }
  }

  handleSelectionCleared(): void {
    //console.log('Selection Cleared');
    // console.log('Cleared Selection');
    this.selectedTextboxBoundsSubject.next(null); // Clear bounds


    const textControls = document.getElementById('text-controls');
    if (textControls) {
      textControls.classList.remove('active');
    } else {
      console.warn("Text controls element not found in the DOM");
    }



    const textControlsMobile = document.getElementById('mobile-controls');
    if (this.isMobileView && textControlsMobile) {
      textControlsMobile.classList.remove('active');
      setTimeout(() => {
        textControlsMobile.style.display = 'none';
      }, 300); // Wait for animation to finish
    }
    this.addHighlightRects();
    this.canvas?.renderAll();
  }

  handleObjectMoving(event: any): void {
    if (!this.canvas || !event.target) return;

    const target = event.target as fabric.Object | undefined;
    if (!target || (target as any).isMarker) return;

    this.clearAlignmentGuides();

    // Update highlights: hide for the moving object, show for others
    this.addHighlightRects();

    // Check for alignment and snap
    const { snappedX, snappedY } = this.checkAlignmentAndSnap(target);

    // Apply snapping
    if (snappedX !== null) {
      target.left = snappedX;
    }
    if (snappedY !== null) {
      target.top = snappedY;
    }

    // Ensure coordinates are updated
    target.setCoords();

    this.canvas.renderAll();
  }

  // handleMouseOver(event: fabric.TPointerEvent): void {
  //    if (!this.canvas) return;

  //   // const overlay = document.getElementById('image-overlay');
  //    const target = event.target as unknown as fabric.Object; // Explicitly cast target to fabric.Object

  //   if (target instanceof fabric.Textbox) {
  //     //console.log('Mouse over textbox: Setting cursor to move');
  //     this.canvas.setCursor('move');
  //   }

  //   // if (target instanceof fabric.Image) {
  //   //   console.log('Mouse over image: Disabling overlay pointer events');
  //   //   if (overlay) {
  //   //     overlay.style.pointerEvents = 'none';
  //   //   }
  //   // }
  // }

  handleMouseOver(event: fabric.TPointerEvent): void {
    if (!this.canvas) return;

    const target = event.target as unknown as fabric.Object | null; // Get the object under mouse
    // console.log('Mouse Over:', target);

    // 🔹 If no object is hovered, apply general canvas highlights
    if (!target) {
      // console.log('calling add highlights');
      this.addHighlightRects();
    }

    // 🔹 If the hovered object is a textbox, change its background color
    if (target instanceof fabric.Textbox) {
      this.canvas.setCursor('move'); // Change cursor to move

      // ✅ Set semi-transparent background to indicate focus
      target.set({
        backgroundColor: 'rgba(245, 245, 245, 0.3)',
        shadow: 'rgba(0, 0, 0, 0.2) 0px 0px 8px',
      });

      this.canvas.renderAll(); // Refresh the canvas
    }
  }




  // Load fonts JSON
  loadFontsJson(filePath: string): Observable<any> {
    return this.http.get(filePath);
  }

  // Apply fonts dynamically
  applyFonts(fontsList: string[]): void {
    fontsList.forEach((fontUrl) => {
      const linkElement = document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = fontUrl;
      document.head.appendChild(linkElement);
    });
  }

  // Apply selected font and size to active object
  applyFontStyle(font: string, fontSize: string, fontweight: string = "normal"): void {
    if (!this.canvas) return;

    const activeObject = this.canvas.getActiveObject();
    if (activeObject && activeObject instanceof fabric.Textbox) {
      activeObject.set({
        fontFamily: font,
        fontSize: parseInt(fontSize, 10),
        fontweight: fontweight
      });
      this.canvas.requestRenderAll();
    } else {
      console.warn('No text object selected or active object is not a Textbox.');
    }
  }



  setUpCanvas(): void {
    if (!this.canvas || !this.canvasConfig) {
      console.error('Canvas or config not available');
      return;
    }

    // Set canvas size from JSON
    this.canvas.setWidth(this.canvasConfig.CanvasWidth);
    this.canvas.setHeight(this.canvasConfig.CanvasHeight);

    // Load and set the background image
    const image = new Image();
    image.crossOrigin = 'Anonymous'
    image.src = this.canvasConfig['imgsrc']; // Ensure the image path is valid


    //console.log('this.canvasConfig.CanvasWidth'+ this.canvasConfig.CanvasWidth);
    //console.log('this.canvasConfig.CanvasHeight'+ this.canvasConfig.CanvasHeight);
    // console.log('this.canvas!.width! / image.width'+ this.canvas!.width! / image.width);
    //console.log('this.canvas!.height! / image.height'+ this.canvas!.height! / image.height);



    // image.onload = () => {
    //   const fabricImg = new fabric.Image(image, {
    //     left: 0,
    //     top: 0,
    //     scaleX: this.canvas!.width! / image.width, // Scale to canvas dimensions
    //     scaleY: this.canvas!.height! / image.height,
    //     selectable: false, // Background image should not be selectable
    //     evented: false, // Disable events for the background image
    //   });

    image.onload = () => {
      const fabricImg = new fabric.Image(image, {
        left: 0,
        top: 0,
        scaleX: this.canvasConfig.CanvasWidth / image.width, // Scale to canvas dimensions
        scaleY: this.canvasConfig.CanvasHeight / image.height,
        selectable: false, // Background image should not be selectable
        evented: false, // Disable events for the background image
      });

      // Add custom property to mark this as the background image
      (fabricImg as any).Background = 1;
      // Set the image as the background
      //this.canvas!.backgroundImage = fabricImg;
      this.moveObjectToBack(fabricImg);

      // Render canvas after setting the background
      this.canvas!.requestRenderAll();
      this.loadUploaderImage(this.canvasConfig);

      // Debug: Log all canvas objects
      const canvasObjects = this.canvas!.getObjects();
      // canvasObjects.forEach((obj, index) => {
      //   console.log(`Layer ${index + 1}:`, obj.type, obj);
      // });
    };

    // Scale the canvas to fit the container
    //if (!this.isMobileView) { //13.04.2025 - Commented Due to aligment issue in mobile
    this.scaleCanvasToFitContainer();
    //}

    image.onerror = () => {
      console.error('Failed to load background image:', this.canvasConfig['imgsrc']);
    };



    // Load textboxes
    this.loadTextboxes(this.canvasConfig.Elements);

    // Load images
    this.loadImages(this.canvasConfig.Elements);
    //this.hideLoader();


    // console.log('Current Background Image:', this.canvas.backgroundImage);
  }

  // addText(text: string): void {
  //   const textbox = new fabric.Textbox(text, { left: 100, top: 100, fontSize: 30, fill: '#000' });
  //   this.canvas?.add(textbox);
  //   this.canvas?.setActiveObject(textbox);
  // }

  addText(): void {
    if (!this.canvas || !this.canvasConfig) {
      console.error('Canvas or CanvasConfig is not initialized.');
      return;
    }

    // Default text for new textboxes
    const textValue = "Add New Text";

    // Default properties for new textboxes
    let fontSize = 30; // Default font size
    let fillColor = this.canvasConfig['hovertheme'] || 'Red'; // Default text color
    let fontFamily = 'Arial'; // Default font family

    // Get properties from the first existing textbox if any
    const existingTextboxes = this.canvas.getObjects().filter(obj => obj.type === 'textbox') as fabric.Textbox[];
    if (existingTextboxes.length > 0) {
      // Use the properties of the first textbox on the canvas
      fontSize = existingTextboxes[0].fontSize || fontSize;
      fillColor = this.canvasConfig['hovertheme'] || fillColor;
      fontFamily = existingTextboxes[0].fontFamily || fontFamily;
    }

    // Increment the textbox count to give each one a unique ID
    this.textboxCount = (this.textboxCount || 0) + 1;

    // Create a new fabric.Textbox with default text and properties
    // const textbox = new fabric.Textbox(textValue, {
    //   left: 100, 
    //   top: 100, 
    //   width: 300, 
    //   height: 50, 
    //   fontSize: fontSize,
    //   fill: fillColor, 
    //   fontFamily: fontFamily, 
    //   hasBorders: true, 
    //   hasControls: true, 
    //   selectable: true, 
    //   evented: true, 
    //   id: `textbox-${this.textboxCount}`, 
    //   name: `textbox-${this.textboxCount}`, 
    //   textAlign: 'center',
    // });

    const textbox = new fabric.Textbox(textValue, {
      left: 100, // Position it horizontally
      top: 100, // Position it vertically
      width: 300, // Set initial width
      height: 50, // Default height
      fontSize: fontSize,
      fill: fillColor, // Default text color
      fontFamily: fontFamily, // Default font family
      hasBorders: true, // Show border around the text for selection
      hasControls: true, // Enable resizing and dragging
      selectable: true, // Allow selection
      evented: true, // Enable events for interaction
      id: `textbox-${this.textboxCount}`, // Unique ID for the textbox
      name: `textbox-${this.textboxCount}`, // Unique name for the textbox
      textAlign: 'center',

      // Allow scaling by default (but only from one control)
      lockScalingX: false,
      lockScalingY: false,
      lockRotation: true,
      cornerStyle: 'circle',
      transparentCorners: false,
    });

    // Show only the top-right control (tr), hide all others
    textbox.setControlsVisibility({
      tl: false, // top-left
      tr: true,  // top-right (scaling allowed)
      bl: false, // bottom-left
      br: false, // bottom-right
      ml: false, // middle-left
      mt: false, // middle-top
      mr: false, // middle-right
      mb: false, // middle-bottom
      mtr: false // rotation control
    });

    // Add the new textbox to the canvas
    this.canvas.add(textbox);

    // Set the newly added text as the active object
    this.canvas.setActiveObject(textbox);

    this.updateControls(textbox);
    // Enable dragging by setting canvas.selection = true (required for group selections)
    this.canvas.selection = true; // Allow dragging of individual or multiple selected objects

    // Ensure the canvas re-renders
    this.canvas.renderAll();
    this.canvas.fire('selection:created', { selected: [textbox] });
    // Debugging: Check if textbox is added and selectable
    // console.log('Textbox added:', textbox);
  }

  updateTextboxColor(event: Event): void {
    const color = (event.target as HTMLInputElement).value;

    if (!this.canvas) {
      console.error('Canvas is not initialized');
      return;
    }

    const activeObject = this.canvas.getActiveObject();
    if (activeObject && activeObject.type === 'textbox') {
      const textbox = activeObject as fabric.Textbox;
      textbox.set('fill', color); // Update text color
      this.canvas.renderAll(); // Re-render the canvas
    } else {
      console.warn('No textbox selected to update color');
    }
  }

  addImageToCanvas(event: any): void {
    const file = event.target.files[0];

    if (file) {
      // console.log('File:', file); // Debug log
      const reader = new FileReader();
      reader.onload = (e: any) => {
        // console.log('FileReader loaded');  // Debug log
        // console.log('e.target.result:', e.target.result); // Debug log

        const dataUrl = e.target.result;

        // Load the image using Fabric.js
        const image = new Image();
        image.crossOrigin = 'Anonymous'
        image.src = dataUrl;

        image.onload = () => {
          // Create the Fabric image
          const img = new fabric.Image(image, {
            left: 100,
            top: 100,
            //height: this.canvasConfig.CanvasHeight / 4, // Set based on canvasConfig
            //width: this.canvasConfig.CanvasWidth / 4, // Set based on canvasConfig
            opacity: 1,
            hasBorders: true,
            hasControls: true,
            lockScalingFlip: true,
            selectable: true,
            evented: true,
            visible: true,
          });

          // Add custom properties
          (img as any).Uploader = 0;
          (img as any).base64 = dataUrl;
          (img as any).src = this.combinePath(this.serverStoragePath, file.name);
          // console.log('img.base64:', (img as any).base64);


          // Add image to canvas and set it as active object
          this.canvas?.add(img);
          this.canvas?.setActiveObject(img);
        };

        image.onerror = (err) => {
          console.error('Error loading image:', err);
        };
      };

      reader.readAsDataURL(file);

      // Handle overlay
      const overlay = document.getElementById('image-overlay');
      if (overlay) {
        overlay.style.pointerEvents = 'none';  // Disable clicking on the overlay
        // No need to set display: 'none' unless explicitly required
      }
    } else {
      console.error('No file selected.');
    }
  }




  // uploadImage(event: any): void {
  //   console.log("uploadImage function triggered!");

  //   const uploader = document.getElementById('image-uploader') as HTMLInputElement;
  //   if (!uploader || !uploader.files || uploader.files.length === 0) {
  //     console.log("No file selected.");
  //     return;
  //   }

  //   const file = uploader.files[0];
  //   console.log("File selected:", file.name); // Log the file name

  //   const reader = new FileReader();
  //   reader.onload = (e: any) => {
  //     //console.log('Image data URL:', e.target.result);
  //     console.log('Base64 Loaded');
  //     // Create the image as a fabric.Image object
  //     const image = new Image();
  //     image.crossOrigin = 'Anonymous'
  //     image.src = e.target.result;

  //     image.onload = () => {
  //       const img = new fabric.Image(image, {
  //         left: 100,
  //         top: 100,
  //         opacity: 1,
  //         hasBorders: true,
  //         hasControls: true,
  //         lockScalingFlip: true,
  //         selectable: true,
  //         evented: true,
  //         visible: true,
  //         Uploader: 1, // Custom property
  //         base64: e.target.result, // Store base64 string
  //       });



  //       // Get image position and dimensions from JSON
  //       const imageProps = this.canvasConfig.Elements.find(
  //         (e: any) => e.id === "image-uploader"
  //       ).properties;

  //       const left = imageProps?.left || 120;
  //       const top = imageProps?.top || 70;
  //       const width = imageProps?.width || 100;
  //       const height = imageProps?.height || 100;

  //       console.log(`Image position - left: ${left}, top: ${top}, width: ${width}, height: ${height}`);

  //       // Scale the image
  //       //img.scaleToWidth(width);
  //       //img.scaleToHeight(height);

  //       // Set additional properties
  //       img.set({
  //         left,
  //         top,
  //       });

  //       // Add the image to the canvas
  //       this.canvas?.add(img);

  //       // Send the image to the back
  //       this.moveObjectToBack(img);

  //       // Refresh the canvas
  //       this.canvas?.renderAll();

  //       // Set as the active object
  //       this.canvas?.setActiveObject(img);

  //       // Hide the uploader
  //       uploader.style.display = 'none';


  //       //DELETE THIS LATER---
  //       const objt = this.canvas?.getObjects(); // Get all objects on the canvas
  //       let found = false;

  //       objt?.forEach((obj) => {
  //         if (obj.type === 'image' && (obj as any).Uploader === 1) {
  //           console.log('Uploader property found on object:', obj);
  //           found = true;
  //         }
  //       });


  //       //END DELET ETHIS LATER--




  //       // Disable and hide the overlay
  //       const overlay = document.getElementById('image-overlay');
  //       if (overlay) {
  //         overlay.style.pointerEvents = 'none';
  //         overlay.style.display = 'none';
  //       }

  //       console.log("Image added to canvas:", img);
  //     };

  //     image.onerror = (err) => {
  //       console.error("Error loading image:", err);
  //       alert("Failed to load the image. Please try again.");
  //     };
  //   };

  //   reader.onerror = (error) => {
  //     console.error("Error reading the file:", error);
  //     alert("An error occurred while reading the image file.");
  //   };

  //   reader.readAsDataURL(file); // Read the file as data URL
  // }

  uploadImage(event: any): void {
    const uploader = document.getElementById('image-uploader') as HTMLInputElement;
    if (!uploader || !uploader.files || uploader.files.length === 0) {
      return;
    }

    const file = uploader.files[0];
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const image = new Image();
      image.crossOrigin = 'Anonymous'
      image.src = e.target.result;

      image.onload = () => {
        const img = new fabric.Image(image, {
          opacity: 1,
          hasBorders: true,
          hasControls: true,
          lockScalingFlip: true,
          selectable: true,
          evented: true,
          visible: true,
          Uploader: 1,
          base64: e.target.result,
        });

        // Get image position from JSON
        const imageProps = this.canvasConfig.Elements.find(
          (e: any) => e.id === "image-uploader"
        ).properties;

        const uploaderLeft = imageProps?.left || 120;
        const uploaderTop = imageProps?.top || 70;

        // Get canvas dimensions
        const canvasWidth = this.canvasConfig.CanvasWidth;
        const canvasHeight = this.canvasConfig.CanvasHeight;
        const uploaderWidth = 100;
        const uploaderHeight = 100;
        // Calculate scaling if image width is larger than half the canvas width
        const maxWidth = canvasWidth / 2;
        let scale = 1;
        if (image.width > maxWidth) {
          scale = maxWidth / image.width;
        }

        // Apply scaling while preserving aspect ratio
        img.scale(scale);

        // Calculate the scaled dimensions
        const scaledWidth = image.width * scale;
        const scaledHeight = image.height * scale;

        // Center the image at uploader's position
        const left = uploaderLeft + (uploaderWidth / 2) - scaledWidth / 2;
        const top = uploaderTop + (uploaderHeight / 2) - scaledHeight / 2;

        // Set position
        img.set({
          left,
          top,
        });

        // Add the image to the canvas
        this.canvas?.add(img);

        // Send the image to the back
        this.moveObjectToBack(img);

        // Refresh the canvas
        this.canvas?.renderAll();

        // Set as the active object
        this.canvas?.setActiveObject(img);

        // Hide the uploader
        uploader.style.display = 'none';

        // Check for uploader property
        const objt = this.canvas?.getObjects();
        let found = false;
        objt?.forEach((obj) => {
          if (obj.type === 'image' && (obj as any).Uploader === 1) {
            found = true;
          }
        });

        // Disable and hide the overlay
        const overlay = document.getElementById('image-overlay');
        if (overlay) {
          overlay.style.pointerEvents = 'none';
          overlay.style.display = 'none';
        }
      };

      image.onerror = (err) => {
        console.error("Error loading image:", err);
        alert("Failed to load the image. Please try again.");
      };
    };

    reader.onerror = (error) => {
      console.error("Error reading the file:", error);
      alert("An error occurred while reading the image file.");
    };

    reader.readAsDataURL(file);
  }


  addImageFromBase64(base64: string): void {
    if (!this.canvas) {
      console.error('Canvas not initialized');
      return;
    }

    const image = new Image();
    image.crossOrigin = 'Anonymous'
    image.src = base64;
    image.onload = () => {
      const img = new fabric.Image(image, {
        left: 100,
        top: 100,
        selectable: true,
      });

      this.canvas?.add(img);
      this.canvas?.requestRenderAll();
      this.hideImageUploader(); // Hide uploader after image is added
    };
  }




  toggleBold(): void {
    const activeObject = this.canvas?.getActiveObject() as fabric.Textbox;
    if (activeObject) {
      activeObject.set({ fontWeight: activeObject.fontWeight === 'bold' ? 'normal' : 'bold' });
      this.canvas?.renderAll();
    }
  }

  toggleItalic(): void {
    const activeObject = this.canvas?.getActiveObject() as fabric.Textbox;
    if (activeObject) {
      activeObject.set({ fontStyle: activeObject.fontStyle === 'italic' ? 'normal' : 'italic' });
      this.canvas?.renderAll();
    }
  }

  alignText(align: string): void {
    const activeObject = this.canvas?.getActiveObject() as fabric.Textbox;
    if (activeObject) {
      activeObject.set({ textAlign: align });
      this.canvas?.renderAll();
    }
  }
  private loadTextboxes(elements: any[]): void {
    elements.forEach((element) => {
      if (element.type === 'Textbox') {
        const properties = element.properties;
        // const textbox = new fabric.Textbox(properties.text, {
        //   left: properties.left,
        //   top: properties.top,
        //   scaleX: properties.scaleX ?? null,
        //   scaleY: properties.scaleY ?? null,
        //   fontSize: properties.fontSize,
        //   fontFamily: properties.fontFamily,
        //   fill: properties.fill,
        //   textAlign: properties.textAlign,
        //   fontWeight: properties.fontWeight,
        //   fontStyle: properties.fontStyle,
        //   underline: properties.underline,
        //   width: properties.width,
        //   editable: !this.isMobileView, //inmobile view we dont wna textbox to be editable only willbe edited using edit popup
        // });
        const textbox = new fabric.Textbox(properties.text, {
          left: properties.left,
          top: properties.top,
          scaleX: properties.scaleX ?? 1,
          scaleY: properties.scaleY ?? 1,
          fontSize: properties.fontSize,
          fontFamily: properties.fontFamily,
          fill: properties.fill,
          textAlign: properties.textAlign,
          fontWeight: properties.fontWeight,
          fontStyle: properties.fontStyle,
          underline: properties.underline,
          width: properties.width,
          lineHeight: properties.lineHeight ?? 1,
          lockRotation: true,
          hasBorders: true,
          hasControls: true,
          selectable: true,
          cornerStyle: 'circle',
          padding: 5,
          transparentCorners: false
        });
        // Only allow scaling from top-right
        textbox.setControlsVisibility({
          tl: false,
          tr: true,
          bl: false,
          br: false,
          ml: false,
          mt: false,
          mr: false,
          mb: false,
          mtr: false
        });
        this.canvas?.add(textbox);
      }
    });
  }

  private loadImages(elements: any[]): void {
    elements.forEach((element) => {
      if (element.type === 'image' && element.properties.base64 && element.properties.Background != 1 && element.properties.Uploader === 0) {

        // Create an Image element
        const image = new Image();
        image.crossOrigin = 'Anonymous'
        image.src = element.properties.base64;

        image.onload = () => {
          // Once the image is loaded, create a Fabric image object
          const img = new fabric.Image(image, {
            left: element.properties.left,
            top: element.properties.top,
            // scaleX: element.properties.width / image.width, // Scale to JSON dimensions
            // scaleY: element.properties.height / image.height,
            opacity: element.properties.opacity ?? 1, // Handle undefined opacity
            selectable: true, // Allow selection
          });

          // Add custom property (Uploader)
          (img as any).Uploader = element.properties.Uploader;

          // Add the image to the canvas
          this.canvas?.add(img);

          // Send to back if required
          // if (element.properties.Uploader === 0) {
          //   this.bringToBack(img);
          // }

          // Render the canvas
          this.canvas?.renderAll();

        };

        image.onerror = (err) => {
          console.error('Failed to load image from Base64:', err);

        };
      }
    });
  }










  private scaleCanvasToFitContainer(): void {
    if (!this.canvas) {
      console.error('Canvas is not initialized.');
      return;
    }

    // Get container dimensions
    const container = document.getElementById('canvas-container');
    if (!container) {
      console.error('Canvas container not found.');
      return;
    }

    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    // Get canvas original dimensions
    const originalWidth = this.canvas.width || 1; // Avoid division by zero
    const originalHeight = this.canvas.height || 1;

    // Calculate the scaling factor to fit the container
    const scaleX = containerWidth / originalWidth;
    const scaleY = containerHeight / originalHeight;
    const scaleFactor = Math.min(scaleX, scaleY); // Maintain aspect ratio

    // Scale the canvas
    this.canvas.setZoom(scaleFactor);

    // Adjust canvas size to fit within the container
    this.canvas.setWidth(originalWidth * scaleFactor);
    this.canvas.setHeight(originalHeight * scaleFactor);

    this.canvas.requestRenderAll(); // Render after scaling
  }



  private loadUploaderImage(canvasConfig: any): void {
    //console.log('LoadUploaderImage');
    const imageElement = canvasConfig.Elements.find(
      (e: any) => e.type === 'image' && e.properties.Uploader === 1
    );
    const uploaderElement = canvasConfig.Elements.find(
      (e: any) => e.type === 'image-uploader'
    );

    const uploader = document.getElementById('image-uploader');


    if (imageElement) {
      if (imageElement.properties.base64) {
        // If base64 exists, show the image
        const image = new Image();
        image.crossOrigin = 'Anonymous'
        image.src = imageElement.properties.base64;
        //image.src = imageElement.properties.src;

        image.onload = () => {
          // Once the image is loaded, create a Fabric image object
          const img = new fabric.Image(image, {
            left: imageElement.properties.left,
            top: imageElement.properties.top,
            // scaleX: imageElement.properties.width / image.width, // Scale to JSON dimensions
            // scaleY: imageElement.properties.height / image.height,
            opacity: imageElement.properties.opacity ?? 1, // Handle undefined opacity
            selectable: true, // Allow selection
          });

          // Set custom properties (Uploader and base64)
          (img as any).Uploader = imageElement.properties.Uploader;
          (img as any).base64 = imageElement.properties.base64;
          (img as any).src = imageElement.properties.src || "";
          // Add image to canvas
          this.canvas?.add(img);

          // Move image to the back
          this.moveObjectToBack(img);

          // Re-render the canvas
          this.canvas?.requestRenderAll();

          // console.log('Image loaded and added to canvas');
          this.hideLoader();
        };

        // Hide the uploader
        if (uploader) uploader.style.display = 'none';
        //this.hideLoader();
      } else {
        // If no image is present, show the uploader
        this.positionUploaderAndOverlay();
        if (uploader) uploader.style.display = 'block';
        //this.hideLoader();

      }
    } else if (canvasConfig.WithPhoto === 1 && uploaderElement) {
      if (uploader) {
        uploader.style.display = 'block';
        this.positionUploaderAndOverlay();
        uploader.style.left = `${uploaderElement.properties.uploaderLeft}px`;
        uploader.style.top = `${uploaderElement.properties.uploaderTop}px`;
        //this.hideLoader();
      }
    }
  }





  private moveObjectToBack(object: fabric.Object): void {
    if (!this.canvas) return;

    // Get all objects on the canvas
    const objects = this.canvas.getObjects();

    // Remove the object
    this.canvas.remove(object);

    // Insert the object at the first position
    this.canvas.add(object);
    //console.log('aas  ');
    this.canvas._objects.unshift(this.canvas._objects.pop()!); // Move the last object to the beginning

    // Re-render the canvas
    this.canvas.requestRenderAll();
  }

  moveCloneObjectToBack(canvas: fabric.StaticCanvas, object: fabric.Object): void {
    const objects = canvas.getObjects();
    const index = objects.indexOf(object);

    if (index > -1) {
      objects.splice(index, 1);
      objects.unshift(object);
      canvas.renderAll();
    }
  }

  deleteSelectedObject(): void {
    if (!this.canvas) {
      console.error('Canvas not initialized');
      return;
    }

    const activeObject = this.canvas.getActiveObject();

    if (activeObject) {

      if (activeObject.type === 'image' && (activeObject as any).Uploader === 1) {
        // console.log('Show Uploader');
        this.showImageUploader(); // Show uploader if an image is deleted 
      }
      this.positionUploaderAndOverlay();
      //this.attachHtmlElementToCanvas()
      this.canvas.remove(activeObject);
      // console.log('Object deleted:', activeObject);
      this.canvas.discardActiveObject(); // Clear the selection
      this.canvas.requestRenderAll(); // Update the canvas
    } else {
      console.warn('No object selected for deletion');
    }
  }


  showImageUploader(): void {
    const uploader = document.getElementById('image-uploader');
    if (uploader /*&& overlay*/) {
      // console.log('Enabling the Uploader');
      uploader.style.display = 'block';
      //overlay.style.display = 'block';
    }
  }

  hideImageUploader(): void {
    const uploader = document.getElementById('image-uploader');
    const overlay = document.getElementById('image-overlay');
    if (uploader && overlay) {
      uploader.style.display = 'none';
      overlay.style.display = 'none';
    }
  }


  positionUploaderAndOverlay(): void {
    const canvasElement = this.canvas.getElement(); // Get the actual canvas element
    const uploader = document.getElementById('image-uploader');
    const uploaderDiv = document.getElementById('image-uploader-image');
    const rectObject = this.canvas.getObjects().find(
      (obj) => obj.type === 'rect' && (obj as any).data === 'UploaderPlaceholder'
    );

    if (!canvasElement || !uploader || !uploaderDiv || !rectObject) {
      console.error('Canvas element, uploader, or rectangle object not found.');
      return;
    }

    // Get the canvas element's bounding rectangle (relative to the screen)
    const canvasRect = canvasElement.getBoundingClientRect();

    // Get the current viewport transform
    const viewportTransform = this.canvas.viewportTransform;
    if (!viewportTransform) {
      console.error('Canvas viewportTransform is not defined.');
      return;
    }

    // Transform the rectangle's position to screen-relative coordinates
    const rectScreenPosition = fabric.util.transformPoint(
      { x: rectObject.left! + rectObject.width! / 2, y: rectObject.top! + rectObject.height! / 2 },
      viewportTransform
    );

    // Calculate the height of the header and text controls
    const headerElement = document.querySelector('.toolbar.toolbar-top');
    const textControlsElement = document.querySelector('.text-controls');

    const headerHeight = headerElement ? headerElement.getBoundingClientRect().height : 0;
    const textControlsHeight = textControlsElement ? textControlsElement.getBoundingClientRect().height : 0;

    // Debugging logs
    // console.log('Canvas Element Bounding Rect:', canvasRect);
    // console.log('Viewport Transform:', viewportTransform);
    // console.log('Rectangle Left (Canvas):', rectObject.left);
    // console.log('Rectangle Top (Canvas):', rectObject.top);
    // console.log('Transformed X (Screen):', rectScreenPosition.x);
    // console.log('Transformed Y (Screen):', rectScreenPosition.y);
    // console.log('Header Height:', headerHeight);
    // console.log('Text Controls Height:', textControlsHeight);

    // Calculate the exact screen position, adjusting for the header and text controls height
    const finalLeft = canvasRect.left + rectScreenPosition.x - rectObject.width! / 2;
    const finalTop = canvasRect.top + rectScreenPosition.y - rectObject.height! / 2 - headerHeight - textControlsHeight;

    // Debugging logs for final position
    //console.log('Final Left (Screen):', finalLeft);
    //console.log('Final Top (Screen):', finalTop);

    // Adjust the uploader's position
    uploader.style.left = `${finalLeft}px`;
    uploader.style.top = `${finalTop}px`;
    uploader.style.position = 'absolute';

    // Adjust the uploaderDiv's position
    uploaderDiv.style.left = `${finalLeft}px`;
    uploaderDiv.style.top = `${finalTop}px`;
    uploaderDiv.style.position = 'absolute';
  }

  initializeUploaderPositioning(): void {
    // Update position when the canvas is redrawn (e.g., zoom or pan)
    this.canvas.on('after:render', () => {
      this.positionUploaderAndOverlay();
    });

    // Update position when zoom is triggered
    // this.canvas.on('mouse:wheel', (opt) => {

    //   console.log("Mouse Wheel");
    //   const delta = opt.e.deltaY;
    //   const zoom = this.canvas.getZoom();
    //   const newZoom = zoom * (delta > 0 ? 0.9 : 1.1);

    //   // Set new zoom level with boundaries
    //   this.canvas.setZoom(Math.max(0.5, Math.min(3, newZoom)));
    //   opt.e.preventDefault();
    //   opt.e.stopPropagation();

    //   this.positionUploaderAndOverlay();
    // });

    window.addEventListener('resize', () => {
      console.log('Window resized or zoom changed');
      this.positionUploaderAndOverlay();
    });

    // Initial positioning
    this.positionUploaderAndOverlay();
  }










  // Listen to window resize events
  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.positionUploaderAndOverlay();
  }



  handleOutsideClick(event: MouseEvent): void {
    //console.log('Outside Click Handler Invoked');

    // Get relevant elements
    const canvasElement = document.querySelector('#canvas') as HTMLElement;
    const controlsElement = document.querySelector('#controls') as HTMLElement;
    const canvasContainerElement = document.querySelector('#canvas-container') as HTMLElement;

    if (!canvasElement || !controlsElement || !canvasContainerElement) {
      console.error('Required elements not found.');
      return;
    }

    // Get bounding rectangles
    const canvasBounds = canvasElement.getBoundingClientRect();
    const controlsBounds = controlsElement.getBoundingClientRect();
    const canvasContainerBounds = canvasContainerElement.getBoundingClientRect();

    // Check if the click is inside the canvas-container
    const isInsideCanvasContainer = (event.clientX >= canvasContainerBounds.left &&
      event.clientX <= canvasContainerBounds.right &&
      event.clientY >= canvasContainerBounds.top &&
      event.clientY <= canvasContainerBounds.bottom);

    // Check if the click is directly on the canvas
    const isOnCanvas = (event.clientX >= canvasBounds.left &&
      event.clientX <= canvasBounds.right &&
      event.clientY >= canvasBounds.top &&
      event.clientY <= canvasBounds.bottom);

    // Check if the click is outside the controls
    const isOutsideControls = !(event.clientX >= controlsBounds.left &&
      event.clientX <= controlsBounds.right &&
      event.clientY >= controlsBounds.top &&
      event.clientY <= controlsBounds.bottom);

    // Final condition: Inside canvas-container, outside canvas, and outside controls
    if (isInsideCanvasContainer && !isOnCanvas && isOutsideControls) {
      this.canvas?.discardActiveObject(); // Deselect any active object
      this.canvas?.renderAll(); // Re-render the canvas
      // console.log('Clicked inside canvas-container but outside canvas and controls. Deselecting objects.');
    } else {
      // console.log('Click not meeting criteria for deselection.');
    }
  }











  // downloadAsPNGWithBackgroundReplacement(projectID: string): void {
  //   if (!this.canvas) {
  //     console.error('Canvas is not initialized.');
  //     return;
  //   }
  //   const backgroundReplacementUrl = 'assets/background-replacement.json';

  //   this.http.get<any[]>(backgroundReplacementUrl).subscribe({
  //     next: (backgroundData) => {
  //       const replacementEntry = backgroundData.find((entry) => entry.ProjectID === projectID);

  //       if (!replacementEntry) {
  //         console.error(`No matching replacement entry found for ProjectID: ${projectID}`);
  //         return;
  //       }

  //       const replacementImageURL = replacementEntry.ReplacementImage;
  //       console.log('Found Replacement Image');

  //       const replacementImage = new Image();
  //       replacementImage.crossOrigin = 'Anonymous';
  //       replacementImage.src = replacementImageURL;

  //       replacementImage.onload = () => {
  //         const tempCanvasElement = document.createElement('canvas');
  //         const tempCanvas = new fabric.StaticCanvas(tempCanvasElement, {
  //           width: this.canvas!.getWidth(),
  //           height: this.canvas!.getHeight(),
  //         });

  //         const backgrdounImage: fabric.Image[] = [];
  //         const uploader1Images: fabric.Image[] = [];
  //         const otherImages: fabric.Image[] = [];
  //         const textObjects: fabric.Textbox[] = [];
  //         console.log('Now iterating over objects.');

  //         this.canvas!.getObjects().forEach((obj) => {
  //           if (obj.type === 'textbox' && obj instanceof fabric.Textbox) {
  //             const clonedTextbox = new fabric.Textbox(obj.text || '', {
  //               left: obj.left,
  //               top: obj.top,
  //               scaleX:obj.scaleX ?? null,
  //               scaleY:obj.scaleY ?? null,
  //               fontSize: obj.fontSize,
  //               fontFamily: obj.fontFamily,
  //               fill: obj.fill,
  //               width: obj.width,
  //               angle: obj.angle,
  //               textAlign: obj.textAlign,
  //               fontWeight: obj.fontWeight,
  //               fontStyle: obj.fontStyle,
  //               underline: obj.underline,
  //               selectable: false,
  //             });
  //             textObjects.push(clonedTextbox);
  //           } else if (obj.type === 'image' && obj instanceof fabric.Image) {
  //             const clonedImage = new fabric.Image(obj.getElement(), {
  //               left: obj.left,
  //               top: obj.top,
  //               scaleX: obj.scaleX,
  //               scaleY: obj.scaleY,
  //               angle: obj.angle,
  //               width: obj.width,
  //               height: obj.height,
  //             });

  //             //Uploader image which goes behind backgrdoun image
  //             if ((obj as any).Uploader === 1) {
  //               uploader1Images.push(clonedImage);
  //             } 
  //             // Other images added on canvas apart from the Uploader image and backgrdound image
  //             if (((obj as any).Background===undefined ||(obj as any).Background!==1 ) && ((obj as any).Uploader !== 1))
  //               {
  //               console.log('Other Images added');
  //               otherImages.push(clonedImage);
  //             }
  //             if ((obj as any).Background===1){
  //               console.log('background Image');
  //               backgrdounImage.push(clonedImage);
  //             }
  //           }
  //         });

  //         console.log('Object iteration completed.');

  //         const bgImage = new Image();
  //         bgImage.crossOrigin = 'Anonymous';
  //         bgImage.src = replacementImageURL;

  //         bgImage.onload = () => {
  //           const fabricBgImage = new fabric.Image(bgImage, {
  //             left: 0,
  //             top: 0,
  //             width: tempCanvas.width,
  //             height: tempCanvas.height,
  //           });

  //           // const scaleX = tempCanvas.width! / bgImage.width;
  //           // const scaleY = tempCanvas.height! / bgImage.height;
  //           // const scale = Math.max(scaleX, scaleY);

  //           //fabricBgImage.scale(scale);
  //           fabricBgImage.set({
  //             left: 0,
  //             top: 0,
  //           });


  //           //Add Uploader Image to canvas if it exists
  //           uploader1Images.forEach((img) => {
  //             console.log('Adding UploaderImage to canvas');
  //             tempCanvas.add(img);
  //             //this.moveObjectToBack(img); // Move the background image to the back
  //           });

  //           // Add the new background image to the canvas
  //           console.log('Ading backgrdoun image to canvas fabricBgImage');
  //           tempCanvas.add(fabricBgImage);


  //           // Add all other images to canvas if they exist
  //           otherImages.forEach((img) => {
  //             console.log('Adding Other Images to canvas');
  //             tempCanvas.add(img);
  //           });

  //           textObjects.forEach((textObj) => {
  //             console.log('Adding textboxes to canvas');
  //             tempCanvas.add(textObj);
  //           });

  //           tempCanvas.renderAll();

  //           setTimeout(() => {
  //             const dataUrl = tempCanvas.toDataURL({
  //               format: 'png',
  //               multiplier: 1,
  //             });
  //             const downloadAnchor = document.createElement('a');
  //             downloadAnchor.href = dataUrl;
  //             downloadAnchor.download = 'card-image.png';
  //             downloadAnchor.click();
  //           }, 500);
  //         };

  //         bgImage.onerror = () => {
  //           console.error('Failed to load replacement image.');
  //           alert('Could not load the replacement background image.');
  //         };
  //       };
  //     },
  //     error: (err) => {
  //       console.error('Error fetching background replacement data:', err);
  //       alert('An error occurred while fetching the background replacement data.');
  //     },
  //   });
  // }



  downloadAsPNGWithBackgroundReplacement(replacementImageURL: string): void {
    const replacementImage = new Image();
    replacementImage.crossOrigin = 'Anonymous';
    replacementImage.src = replacementImageURL;
    //const canvasWidth = this.canvas!.getWidth(); --JOHN 08042025
    //const canvasHeight = this.canvas!.getHeight();--JOHN 08042025
    const canvasWidth = this.canvasConfig.CanvasWidth;
    const canvasHeight = this.canvasConfig.CanvasHeight;
    replacementImage.onload = () => {
      //console.log(`Replacement Image Dimensions: Width = ${replacementImage.width}, Height = ${replacementImage.height}`);

      const tempCanvasElement = document.createElement('canvas');
      const tempCanvas = new fabric.StaticCanvas(tempCanvasElement, {
        width: canvasWidth,
        height: canvasHeight,
      });




      // console.log(`Canvas Dimensions: Width = ${canvasWidth}, Height = ${canvasHeight}`);

      const scaleX = canvasWidth / replacementImage.width;
      const scaleY = canvasHeight / replacementImage.height;
      const replacementImageHeight = replacementImage.height;
      const replacementImageWidth = replacementImage.width;
      // console.log(`Scaling Factor: scaleX = ${scaleX}, scaleY = ${scaleY}`);
      // console.log(`replacementImageHeight = ${replacementImageHeight}, replacementImageWidth = ${replacementImageWidth}`);

      const backgrdounImage: fabric.Image[] = [];
      const uploader1Images: fabric.Image[] = [];
      const otherImages: fabric.Image[] = [];
      const textObjects: fabric.Textbox[] = [];
      // console.log('Now iterating over objects.');

      this.canvas!.getObjects().forEach((obj) => {
        if (obj.type === 'textbox' && obj instanceof fabric.Textbox) {
          // console.log('obj.type,' + obj.type);
          // console.log('left: obj.left,' + obj.left);
          // console.log('left: obj.top,' + obj.top);
          // console.log('obj.scaleX,' + obj.scaleX);
          // console.log('obj.scaleY,' + obj.scaleY);
          const clonedTextbox = new fabric.Textbox(obj.text || '', {
            left: obj.left,
            top: obj.top,
            scaleX: obj.scaleX ?? null,
            scaleY: obj.scaleY ?? null,
            fontSize: obj.fontSize,
            fontFamily: obj.fontFamily,
            fill: obj.fill,
            width: obj.width,
            angle: obj.angle,
            textAlign: obj.textAlign,
            fontWeight: obj.fontWeight,
            fontStyle: obj.fontStyle,
            underline: obj.underline,
            selectable: false,
          });
          textObjects.push(clonedTextbox);
        } else if (obj.type === 'image' && obj instanceof fabric.Image) {
          // console.log('obj.type,' + obj.type);
          // console.log('left: obj.left,' + obj.left);
          // console.log('left: obj.top,' + obj.top);
          // console.log('obj.scaleX,' + obj.scaleX);
          // console.log('obj.scaleY,' + obj.scaleY);
          const clonedImage = new fabric.Image(obj.getElement(), {
            left: obj.left,
            top: obj.top,
            scaleX: obj.scaleX,
            scaleY: obj.scaleY,
            angle: obj.angle,
            width: obj.width,
            height: obj.height,
          });

          if ((obj as any).Uploader === 1) {
            uploader1Images.push(clonedImage);
          }

          if (((obj as any).Background === undefined || (obj as any).Background !== 1) && ((obj as any).Uploader !== 1)) {
            // console.log('Other Images added');
            otherImages.push(clonedImage);
          }

          if ((obj as any).Background === 1) {
            // console.log('background Image');
            backgrdounImage.push(clonedImage);
          }
        }
      });

      // console.log('Object iteration completed.');

      const bgImage = new fabric.Image(replacementImage, {
        left: 0,
        top: 0,
        scaleX: scaleX,
        scaleY: scaleY,
        originX: 'left',
        originY: 'top',
      });

      // Add Uploader Image to canvas if it exists
      uploader1Images.forEach((img) => {
        // console.log('Adding UploaderImage to canvas');
        tempCanvas.add(img);
      });

      // console.log('Adding background image to canvas');
      tempCanvas.add(bgImage);

      otherImages.forEach((img) => {
        // console.log('Adding Other Images to canvas');
        tempCanvas.add(img);
      });

      textObjects.forEach((textObj) => {
        // console.log('Adding textboxes to canvas');
        tempCanvas.add(textObj);
      });

      tempCanvas.renderAll();

      setTimeout(() => {
        const dataUrl = tempCanvas.toDataURL({
          format: 'png',
          multiplier: 1,
        });
        const downloadAnchor = document.createElement('a');
        downloadAnchor.href = dataUrl;
        downloadAnchor.download = 'card-image.png';
        downloadAnchor.click();
      }, 500);
    };

    replacementImage.onerror = () => {
      console.error('Failed to load replacement image.');
      alert('Could not load the replacement background image.');
    };
    // if (!this.canvas) {
    //   console.error('Canvas is not initialized.');
    //   return;
    // }

    // const backgroundReplacementUrl = './assets/background-replacement.json';

    // this.http.get<any[]>(backgroundReplacementUrl).subscribe({
    //   next: (backgroundData) => {
    //     const replacementEntry = backgroundData.find((entry) => entry.ProjectID === projectID);

    //     if (!replacementEntry) {
    //       console.error(`No matching replacement entry found for ProjectID: ${projectID}`);
    //       return;
    //     }

    //     const replacementImageURL = replacementEntry.ReplacementImage;
    //     console.log('Found Replacement Image');


    //   },
    //   error: (err) => {
    //     console.error('Error fetching background replacement data:', err);
    //     alert('An error occurred while fetching the background replacement data.');
    //   },
    // });
  }

  getBase64WithBackgroundReplacement(replacementImageURL: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const replacementImage = new Image();
      replacementImage.crossOrigin = 'Anonymous';
      replacementImage.src = replacementImageURL;
      const canvasWidth = this.canvasConfig.CanvasWidth;
      const canvasHeight = this.canvasConfig.CanvasHeight;
      replacementImage.onload = () => {
        // console.log(`Replacement Image Dimensions: Width = ${replacementImage.width}, Height = ${replacementImage.height}`);

        const tempCanvasElement = document.createElement('canvas');
        const tempCanvas = new fabric.StaticCanvas(tempCanvasElement, {
          width: canvasWidth,
          height: canvasHeight,
        });



        // console.log(`Canvas Dimensions: Width = ${canvasWidth}, Height = ${canvasHeight}`);

        const scaleX = canvasWidth / replacementImage.width;
        const scaleY = canvasHeight / replacementImage.height;
        // console.log(`Scaling Factor: scaleX = ${scaleX}, scaleY = ${scaleY}`);

        const backgrdounImage: fabric.Image[] = [];
        const uploader1Images: fabric.Image[] = [];
        const otherImages: fabric.Image[] = [];
        const textObjects: fabric.Textbox[] = [];
        // console.log('Now iterating over objects.');

        this.canvas!.getObjects().forEach((obj) => {
          if (obj.type === 'textbox' && obj instanceof fabric.Textbox) {
            const clonedTextbox = new fabric.Textbox(obj.text || '', {
              left: obj.left,
              top: obj.top,
              scaleX: obj.scaleX ?? null,
              scaleY: obj.scaleY ?? null,
              fontSize: obj.fontSize,
              fontFamily: obj.fontFamily,
              fill: obj.fill,
              width: obj.width,
              angle: obj.angle,
              textAlign: obj.textAlign,
              fontWeight: obj.fontWeight,
              fontStyle: obj.fontStyle,
              underline: obj.underline,
              selectable: false,
            });
            textObjects.push(clonedTextbox);
          } else if (obj.type === 'image' && obj instanceof fabric.Image) {
            const clonedImage = new fabric.Image(obj.getElement(), {
              left: obj.left,
              top: obj.top,
              scaleX: obj.scaleX,
              scaleY: obj.scaleY,
              angle: obj.angle,
              width: obj.width,
              height: obj.height,
            });

            if ((obj as any).Uploader === 1) {
              uploader1Images.push(clonedImage);
            }

            if (((obj as any).Background === undefined || (obj as any).Background !== 1) && ((obj as any).Uploader !== 1)) {
              // console.log('Other Images added');
              otherImages.push(clonedImage);
            }

            if ((obj as any).Background === 1) {
              // console.log('background Image');
              backgrdounImage.push(clonedImage);
            }
          }
        });

        // console.log('Object iteration completed.');

        const bgImage = new fabric.Image(replacementImage, {
          left: 0,
          top: 0,
          scaleX: scaleX,
          scaleY: scaleY,
          originX: 'left',
          originY: 'top',
        });

        // Add Uploader Image to canvas if it exists
        uploader1Images.forEach((img) => {
          // console.log('Adding UploaderImage to canvas');
          tempCanvas.add(img);
        });

        // console.log('Adding background image to canvas');
        tempCanvas.add(bgImage);

        otherImages.forEach((img) => {
          // console.log('Adding Other Images to canvas');
          tempCanvas.add(img);
        });

        textObjects.forEach((textObj) => {
          // console.log('Adding textboxes to canvas');
          tempCanvas.add(textObj);
        });

        tempCanvas.renderAll();

        setTimeout(() => {
          try {
            const dataUrl = tempCanvas.toDataURL({
              format: 'png',
              multiplier: 1,
            });
            resolve(dataUrl);
          } catch (error) {
            console.error('Error generating base64 image:', error);
            reject(error);
          }
        }, 1000);
      };

      replacementImage.onerror = () => {
        console.error('Failed to load replacement image.');
        reject('Could not load the replacement background image.');
      };
    });
  }













  // --End DOwnload Paid


  //Download Normal PNG
  // downloadAsPNG(): void {
  //   if (!this.canvas) {
  //     console.error('Canvas is not initialized.');
  //     return;
  //   }

  //   // Create a temporary canvas to add watermark without affecting the main canvas
  //   const tempCanvasElement = document.createElement('canvas');
  //   const tempContext = tempCanvasElement.getContext('2d');

  //   if (!tempContext) {
  //     console.error('Failed to get 2D context for temporary canvas.');
  //     return;
  //   }

  //   // Set the temporary canvas dimensions to match the original canvas
  //   tempCanvasElement.width = this.canvas.getWidth();
  //   tempCanvasElement.height = this.canvas.getHeight();

  //   // Draw the original canvas image onto the temporary canvas
  //   const canvasElement = this.canvas.getElement(); // Get the actual DOM element of the canvas
  //   tempContext.drawImage(canvasElement, 0, 0);

  //   // (Optional) Add watermark or other overlay here, if required
  //   // this.addWatermarkToCanvas(tempCanvasElement);

  //   // Wait for any additional overlays (e.g., watermark) to load before exporting as PNG
  //   setTimeout(() => {
  //     // Export the canvas with any overlays to a PNG data URL
  //     const dataUrl = tempCanvasElement.toDataURL('image/png');

  //     // Create a temporary anchor element to trigger the download
  //     const downloadAnchor = document.createElement('a');
  //     downloadAnchor.href = dataUrl;
  //     downloadAnchor.download = 'card-image.png'; // File name
  //     downloadAnchor.click(); // Trigger the download
  //   }, 1000); // Adjust timeout if necessary to ensure overlays are applied
  // }

  downloadAsPNG(): void {
    if (!this.canvas) {
      console.error('Canvas is not initialized.');
      return;
    }

    // Create a temporary canvas for accurate export
    const tempCanvasElement = document.createElement('canvas');
    const tempCanvas = new fabric.StaticCanvas(tempCanvasElement, {
      width: this.canvasConfig.CanvasWidth,
      height: this.canvasConfig.CanvasHeight
    });

    // console.log('Canvas Dimensions:', {
    //   width: this.canvas.getWidth(),
    //   height: this.canvas.getHeight(),
    // });

    // Iterate over all objects and add them to the temporary canvas
    this.canvas.getObjects().forEach((obj) => {
      // Clone each object and add it to the tempCanvas
      if (obj.type === 'textbox' && obj instanceof fabric.Textbox) {
        const clonedTextbox = new fabric.Textbox(obj.text || '', {
          left: obj.left,
          top: obj.top,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          fontSize: obj.fontSize,
          fontFamily: obj.fontFamily,
          fill: obj.fill,
          width: obj.width,
          angle: obj.angle,
          textAlign: obj.textAlign,
          fontWeight: obj.fontWeight,
          fontStyle: obj.fontStyle,
          underline: obj.underline,
          lineHeight: obj.lineHeight ?? 1,
          selectable: false,
        });
        tempCanvas.add(clonedTextbox);
      } else if (obj.type === 'image' && obj instanceof fabric.Image) {
        const clonedImage = new fabric.Image(obj.getElement(), {
          left: obj.left,
          top: obj.top,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          angle: obj.angle,
          width: obj.width,
          height: obj.height,
        });
        tempCanvas.add(clonedImage);
      }
    });

    // Render the temporary canvas
    tempCanvas.renderAll();

    // Export the canvas as PNG
    setTimeout(() => {
      const dataUrl = tempCanvas.toDataURL({
        format: 'png',
        multiplier: 1,
      });

      // console.log('Exported PNG URL:', dataUrl);

      const downloadAnchor = document.createElement('a');
      downloadAnchor.href = dataUrl;
      downloadAnchor.download = 'canvas-image.png';
      downloadAnchor.click();
    }, 500);
  }


  addWatermarkToCanvas(tempCanvasElement: HTMLCanvasElement): void {
    const context = tempCanvasElement.getContext('2d');
    if (context) {
      context.font = '20px Arial';
      context.fillStyle = 'rgba(255, 255, 255, 0.5)';
      context.textAlign = 'right';
      context.fillText('My Watermark', tempCanvasElement.width - 10, tempCanvasElement.height - 10);
    }
  }
  //END Download Normal PNG

  //Saving JSON
  saveJSON(): void {
    const updatedJSON = this.generateUpdatedJSON();
    if (!updatedJSON) {
      console.error("Failed to generate JSON.");
      return;
    }

    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(updatedJSON, null, 2));
    const downloadAnchor = document.createElement("a");
    downloadAnchor.href = dataStr;
    downloadAnchor.download = "card-config.json";
    downloadAnchor.click();
  }

  generateUpdatedJSON(): any {
    if (!this.canvas || !this.canvasConfig) {
      console.error("Canvas or canvasConfig is not initialized.");
      return;
    }
    const updatedJSON = {
      CardMain: {
        ProjectID: "123456",
        CanvasWidth: this.canvas.getWidth(),
        CanvasHeight: this.canvas.getHeight(),
        "imgsrc": this.canvasConfig["imgsrc"],
        WithPhoto: this.canvasConfig["WithPhoto"],
        hovertheme: this.canvasConfig["hovertheme"],
        Elements: [] as Array<any>, // Flexible Elements array
      },
    };

    // Add uploader information
    if (this.canvasConfig.WithPhoto === 1) {
      const uploaderElement = this.canvasConfig.Elements.find(
        (e: any) => e.id === "image-uploader"
      );
      if (uploaderElement) {
        updatedJSON.CardMain.Elements.push({
          id: uploaderElement.id,
          name: uploaderElement.name,
          type: uploaderElement.type,
          properties: { ...uploaderElement.properties },
        });
      }
    }
    // Process canvas objects
    this.canvas.getObjects().forEach((obj) => {
      const commonProperties = {
        left: obj.left,
        top: obj.top,
        scaleX: obj.scaleX,
        scaleY: obj.scaleY,
        angle: obj.angle,
        width: obj.width,
        height: obj.height,
        opacity: obj.opacity,
      };

      if (obj.type === "textbox" && obj instanceof fabric.Textbox) {
        updatedJSON.CardMain.Elements.push({
          type: "Textbox",
          properties: {
            ...commonProperties,
            text: obj.text,
            fontSize: obj.fontSize,
            fontFamily: obj.fontFamily,
            fill: obj.fill,
            textAlign: obj.textAlign,
            fontWeight: obj.fontWeight,
            fontStyle: obj.fontStyle,
            underline: obj.underline,
            lineHeight: obj.lineHeight,
          },
        });
      } else if (obj.type === "image" && obj instanceof fabric.Image) {

        const uploaderFlag = (obj as any).Uploader || 0;
        const Background = (obj as any).Background || 0;
        const src = (obj as any).src || null;
        // Check if the object has an attached SVG string
        const svgString = (obj as any).toSVGString || "";

        let base64;
        try {
          base64 = svgString ? "" : obj.toDataURL(); // Generate a fresh base64 string for the image
        }
        catch {

        }
        // if (Background === 1) {
        //   base64 = "";
        // }
        updatedJSON.CardMain.Elements.push({
          type: "image",
          properties: {
            ...commonProperties,
            Uploader: uploaderFlag,
            src: src || "",
            svg: svgString || "", // Save SVG string if available
            base64: base64 || "",
            Background: Background || "",
          },
        });
      }

      else {
        console.log("Unsupported object type:", obj.type);
      }
    });

    return updatedJSON;
  }



  //Saving JSON

  updateControls(selectedObject: fabric.Textbox): void {
    if (selectedObject && selectedObject.type === 'textbox') {
      // console.log('Updating controls for selected textbox');

      // Update font family
      this.selectedFont = selectedObject.fontFamily || 'Arial';

      // Update font size (convert to string with 'px' if needed)
      this.selectedFontSize = selectedObject.fontSize?.toString() || '16';
      if (!this.selectedFontSize.endsWith('px')) {
        this.selectedFontSize += 'px'; // Ensure consistency with dropdown values
      }

      // Update text color
      this.selectedColor = selectedObject.fill?.toString() || '#000000';

      // Update bold status
      this.isBold =
        selectedObject.fontWeight === 'bold' ||
        (typeof selectedObject.fontWeight === 'number' && selectedObject.fontWeight >= 700);

      // Update italic status
      this.isItalic = selectedObject.fontStyle === 'italic';

      // Update alignment status
      this.isAlignLeft = selectedObject.textAlign === 'left';
      this.isAlignCenter = selectedObject.textAlign === 'center';
      this.isAlignRight = selectedObject.textAlign === 'right';

      this.selectedLineHeight = selectedObject.lineHeight || 1;
      // console.log('Controls updated:', {
      //   font: this.selectedFont,
      //   size: this.selectedFontSize,
      //   color: this.selectedColor,
      //   bold: this.isBold,
      //   italic: this.isItalic,
      //   alignLeft: this.isAlignLeft,
      //   alignCenter: this.isAlignCenter,
      //   alignRight: this.isAlignRight,
      // });
    } else {
      console.warn('Selected object is not a textbox or is invalid.');
    }
  }

  applyLineHeight(lineHeight: number): void {
    if (!this.canvas) {
      console.error('Canvas is not initialized');
      return;
    }

    const activeObject = this.canvas.getActiveObject();
    if (activeObject && activeObject instanceof fabric.Textbox) {
      console.log(`Applying line height ${lineHeight} to textbox:`, activeObject);
      activeObject.set({ lineHeight: lineHeight });
      this.canvas?.requestRenderAll();
      this.selectedLineHeight = lineHeight;
      console.log('Line height applied successfully');
    } else {
      console.warn('No textbox selected or active object is not a Textbox:', activeObject);
    }
  }

  addRectToCanvas(canvasConfig: any): fabric.Rect {
    const uploaderEle = canvasConfig.Elements.find(
      (e: any) => e.type === 'image-uploader'
    );
    const reachLeft = uploaderEle?.properties?.uploaderLeft;
    const reachTop = uploaderEle?.properties?.uploaderTop;
    //  console.log('reachLeft----->' +reachLeft);
    //  console.log('reachTop----->' +reachTop);
    const rect = new fabric.Rect({
      left: reachLeft,
      top: reachTop,
      width: 50,
      height: 50,
      fill: 'rgba(0,0,0,0)', // Transparent for clickable area
      stroke: null, // 'black' Optional visibility for troubleshooting
      strokeWidth: 1,
      selectable: false,
      hasBorders: false,
      hasControls: false,
      data: 'UploaderPlaceholder',
    });
    this.canvas?.add(rect);
    return rect;
  }




  attachHtmlElementToCanvas(
    fabricObject: fabric.Object,
    htmlElement: HTMLElement,
    canvasConfig: any
  ): void {
    if (!this.canvas) {
      console.error('Canvas is not initialized.');
      return;
    }

    // Find the uploader element from the canvas configuration
    const uploaderEle = canvasConfig.Elements.find(
      (e: any) => e.type === 'image-uploader'
    );
    if (!uploaderEle) {
      console.error('Uploader element not found in canvasConfig.');
      return;
    }

    const reachLeft = uploaderEle.properties.uploaderLeft;
    const reachTop = uploaderEle.properties.uploaderTop;

    const updatePosition = () => {
      const canvasOffset = this.canvas!.calcOffset(); // Get the canvas offset
      const zoom = this.canvas!.getZoom(); // Get the current zoom level

      // Transform the Fabric.js object's center coordinates
      const fabricCenter = fabric.util.transformPoint(
        {
          x: fabricObject.left! + fabricObject.width! / 2,
          y: fabricObject.top! + fabricObject.height! / 2,
        },
        this.canvas!.viewportTransform!
      );

      // Update the HTML element's position based on transformations
      htmlElement.style.left = `${canvasOffset.left + fabricCenter.x * zoom}px`;
      htmlElement.style.top = `${canvasOffset.top + fabricCenter.y * zoom}px`;
      htmlElement.style.position = 'absolute';
    };

    // Initial position update
    updatePosition();

    // Update position on canvas render
    this.canvas.on('after:render', updatePosition);

    // Show the HTML element when the Fabric object is clicked
    fabricObject.on('mousedown', () => {
      htmlElement.style.display = 'block';
      htmlElement.focus();
    });

    // Hide the HTML element when clicking outside the Fabric object
    this.canvas.on('mouse:down', (e) => {
      if (e.target !== fabricObject) {
        htmlElement.style.display = 'none';
      }
    });
  }





  showLoader(): void {
    //console.log('Reached Show Loader');
    this.isLoadingSubject.next(true); // Emit `true` to show loader

  }

  // Hide the loader
  hideLoader(): void {
    //console.log('Reached Hide Loader');
    this.isLoadingSubject.next(false); // Emit `false` to hide loader

  }




  // Method to open the popup
  // openTextEditPopup(): void {
  //   if (this.selectedTextbox) {
  //     this.showTextEditPopup = true;
  //     this.selectedTextContent =
  //       this.selectedTextbox.text || '';
  //   } else {
  //     console.warn('No textbox is selected to edit.');
  //   }
  // }

  openTextEditPopup({ text, textAlign }): void {
    if (this.selectedTextbox) {
      this.showTextEditPopup = true;
      this.selectedTextContent = this.selectedTextbox.text || '';
      this.popupTextAlign = this.selectedTextbox.textAlign || 'left'; // Store textAlign
    } else {
      console.warn('No textbox is selected to edit.');
    }
  }

  GetProjectConfig(projectID: string): Observable<string> {
    const jsonFilePath = 'assets/card-assets/background-replacement.json';
    // console.log('Invoking call to fetch the path of JSON');

    return this.http.get<any[]>(jsonFilePath).pipe(
      map((jsonData: any[]) => {
        if (!jsonData || jsonData.length === 0) {
          throw new Error('JSON data is empty or undefined.');
        }

        const ProjectJSON = jsonData.find((entry) => entry.ProjectID === projectID);
        // console.log(ProjectJSON);
        if (!ProjectJSON) {
          return null;
        }

        return ProjectJSON.ProjectJsonPath;
      }),
      catchError((error: any) => {
        console.error('Error fetching background replacement data:', error);
        return throwError(() => new Error('Failed to fetch background replacement data.'));
      })
    );
  }



  uploadBackgroundImage(file: File): void {
    this.canvasConfig = "1";
    // console.log("this.canvasConfig" + this.canvasConfig);
    debugger;
    this.newCardBackground = file.name;
    let ratio = 0;
    let scaleFactor = 0;
    const maxHeight = 784;
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const image = new Image();
      image.src = e.target.result;
      image.crossOrigin = 'Anonymous';
      image.onload = () => {
        // Fetch dimensions of the uploaded image
        let imgWidth = image.width;
        let imgHeight = image.height;

        ratio = image.height / image.width;
        // console.log(`Image dimensions: Width=${imgWidth}, Height=${imgHeight}, Ratio=${ratio}`);
        if (imgHeight > 784) {
          scaleFactor = maxHeight / imgHeight;
          imgWidth = imgWidth * scaleFactor;
          imgHeight = imgHeight * scaleFactor;

          // console.log(`New Image dimensions: Width=${imgWidth}, Height=${imgHeight}, scaleFactor=${scaleFactor}`);
        }

        // Update the canvas dimensions to match the image dimensions
        if (this.canvas) {
          this.canvas.setWidth(imgWidth);
          this.canvas.setHeight(imgHeight);
          this.canvasWidth = imgWidth;
          this.canvasHeight = imgHeight;
        }

        
        // Create a Fabric.js image object
        const img = new fabric.Image(image, {
          selectable: false,
          //base64: e.target.result, for backgrdoun image we dont store
        });

        // Set the Background property
        (img as any).Background = 1;

        // Scale the image to match the canvas size (if necessary)
        img.scaleToWidth(imgWidth);
        img.scaleToHeight(imgHeight);

        // Add image to the canvas
        this.canvas?.add(img);

        // Move image to the back of the canvas
        this.moveObjectToBack(img);

        // Re-render the canvas
        this.canvas?.requestRenderAll();
        // console.log('Background image added to canvas.');
      };
    };

    reader.readAsDataURL(file); // Convert file to Base64 string
  }

  // uploadBackgroundImage(file: File): void {
  //   this.canvasConfig = "1";
  //   console.log("this.canvasConfig" + this.canvasConfig);
  //   this.newCardBackground = file.name;
  //   let ratio = 0;
  //   let scaleFactor = 0;
  //   const maxHeight = 784;

  //   const formData = new FormData();
  //   formData.append('file', file);
  //   this.singleFileList = [];
  //   this.singleFileList.push({ "file": file, "fieldName": file.name.replace(/\.[^/.]+$/, "") });
  //   let objRequestData = Object.assign({});
  //   this.userDetails = JSON.parse(localStorage.getItem("userDetails"));
  //   objRequestData.UserId = (this.userDetails == null || this.userDetails == undefined) ? 0 : this.userDetails.UserId;
  //   // Upload the image to the server
  //   this._httpService.ajaxPostFileList('Personalize/UploadAdminFile', this.singleFileList, objRequestData).then(
  //     (response: any) => {
  //       console.log("Image uploaded successfully:", response);
  //       const imageUrl = this._httpService.baseURL + response.FilePath;; // Assuming response contains the stored image URL

  //       const image = new Image();
  //       image.src = imageUrl;

  //       image.onload = () => {
  //         // Fetch dimensions of the uploaded image
  //         let imgWidth = image.width;
  //         let imgHeight = image.height;

  //         ratio = image.height / image.width;
  //         console.log(`Image dimensions: Width=${imgWidth}, Height=${imgHeight}, Ratio=${ratio}`);

  //         if (imgHeight > maxHeight) {
  //           scaleFactor = maxHeight / imgHeight;
  //           imgWidth *= scaleFactor;
  //           imgHeight *= scaleFactor;
  //           console.log(`New Image dimensions: Width=${imgWidth}, Height=${imgHeight}, scaleFactor=${scaleFactor}`);
  //         }

  //         // Update the canvas dimensions to match the image dimensions
  //         if (this.canvas) {
  //           this.canvas.setWidth(imgWidth);
  //           this.canvas.setHeight(imgHeight);
  //         }

  //         // Create a Fabric.js image object
  //         try {
  //           // fabric.Image.fromURL(imageUrl, { crossOrigin: 'anonymous' }, (img) => {
  //           //   img.set({
  //           //     selectable: false,
  //           //   });

  //           //   // Set the Background property
  //           //   (img as any).Background = 1;

  //           //   // Scale the image to match the canvas size (if necessary)
  //           //   img.scaleToWidth(imgWidth);
  //           //   img.scaleToHeight(imgHeight);

  //           //   // Add image to the canvas
  //           //   this.canvas?.add(img);

  //           //   // Move image to the back of the canvas
  //           //   this.moveObjectToBack(img);

  //           //   // Re-render the canvas
  //           //   this.canvas?.requestRenderAll();
  //           //   console.log('Background image added to canvas.');
  //           // });
  //           const img = new fabric.Image(image, {
  //             selectable: false,
  //             //base64: e.target.result, for backgrdoun image we dont store
  //           });

  //           // Set the Background property
  //           (img as any).Background = 1;

  //           // Scale the image to match the canvas size (if necessary)
  //           img.scaleToWidth(imgWidth);
  //           img.scaleToHeight(imgHeight);

  //           // Add image to the canvas
  //           this.canvas?.add(img);

  //           // Move image to the back of the canvas
  //           this.moveObjectToBack(img);

  //           // Re-render the canvas
  //           this.canvas?.requestRenderAll();
  //         }
  //         catch (error: any) {
  //         }
  //       };
  //     },
  //     (error) => {
  //       console.error("Error uploading image:", error);
  //     }
  //   );
  // }



  setSelectedColor(color: string): void {
    this.newCardHoverTheme = color;
    // console.log('Color stored in service:', this.newCardHoverTheme); // Debugging
  }

  uploadPhoto(file: File): void {
    // console.log("In Photo this.canvasConfig" + this.canvasConfig);
    if (!(file instanceof Blob)) {
      console.error('Invalid file type:', file);
      return;
    }

    const reader = new FileReader();

    reader.onload = (e: any) => {
      const image = new Image();
      image.src = e.target.result;
      image.crossOrigin = 'Anonymous'
      image.onload = () => {
        const imgWidth = image.width;
        const imgHeight = image.height;
        let scaleX = 1;
        let scaleY = 1;

        // Check if image size is greater than half the canvas size
        const maxWidth = this.canvasWidth * 0.5;
        const maxHeight = this.canvasHeight * 0.5;

        // Adjust the scale if the image is too big
        if (image.width > maxWidth || image.height > maxHeight) {
          const scaleFactor = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
          scaleX *= scaleFactor;
          scaleY *= scaleFactor;
        }

        // console.log(`Photo dimensions: Width=${imgWidth}, Height=${imgHeight}`);
        // console.log(`maxWidth=${maxWidth}, maxHeight=${maxHeight}`);
        // console.log(`scaleX=${scaleX}, scaleY=${scaleY}`);

        // Create a Fabric.js image object
        const img = new fabric.Image(image, {
          top: 100,
          left: 100,
          height: this.canvasConfig.CanvasHeight / 4, // Set based on canvasConfig
          width: this.canvasConfig.CanvasWidth / 4, // Set based on canvasConfig
          selectable: true,
          evented: true,
          hasBorders: true,
          hasControls: true,
          lockScalingFlip: true,
          lockMovementX: false, // Ensure horizontal movement is allowed
          lockMovementY: false, // Ensure vertical movement is allowed
          base64: e.target.result,
        });
        (img as any).Uploader = 1;
        (img as any).src = this.combinePath(this.serverStoragePath, file.name);

        // Scale the image to fit within default dimensions (if needed)
        //img.scaleToWidth(imgWidth);
        //img.scaleToHeight(imgHeight);

        // Add the image to the canvas
        this.canvas?.add(img);

        // Move the image to the back of the stack
        this.moveObjectToBack(img);



        // Refresh the canvas
        this.canvas?.requestRenderAll();
        this.newCardWithPhoto = 1;
        console.log('Photo successfully added and positioned behind other objects.');
      };
    };

    reader.readAsDataURL(file); // Convert file to Base64 string
  }



  getPhotoCoordinates(): { top: number; left: number } {
    const activeObject = this.canvas?.getActiveObject() as fabric.Image;
    if (activeObject) {
      return {
        top: activeObject.top || 0,
        left: activeObject.left || 0,
      };
    }
    return { top: 0, left: 0 };
  }

  addPlacementMarker(): void {
    // Create a draggable but non-resizable marker (circle)
    const marker = new fabric.Circle({
      left: 100, // Initial position
      top: 100,
      radius: 10,
      fill: 'red',
      stroke: 'black',
      strokeWidth: 1,
      name: 'placement-marker', // Custom property to identify the marker
      hasBorders: false, // Hides selection borders
      hasControls: false, // Disables resizing and rotation
      selectable: true, // Allows selection and dragging
      evented: true, // Allows interaction
    });

    // Initialize this.marker if it's not already initialized
    if (!this.marker) {
      this.marker = marker;
    }

    // Add a custom property to identify it as a marker
    this.marker.isMarker = true;

    // Add the marker to the canvas
    this.canvas?.add(marker);
    this.canvas?.setActiveObject(marker);
    // console.log('Placement marker added. Drag it to the desired position.');
  }


  private checkAlignmentAndSnap(target: fabric.Object): { snappedX: number | null; snappedY: number | null } {
    if (!this.canvas) return { snappedX: null, snappedY: null };

    // Get canvas dimensions
    const canvasWidth = this.canvas.getWidth();
    const canvasHeight = this.canvas.getHeight();

    // Get current position (use target.left/top directly for movement)
    const targetLeft = target.left || 0;
    const targetTop = target.top || 0;
    const bounds = target.getBoundingRect();
    const targetWidth = bounds.width;
    const targetHeight = bounds.height;
    const targetRight = targetLeft + targetWidth;
    const targetBottom = targetTop + targetHeight;
    const targetCenterX = targetLeft + targetWidth / 2;
    const targetCenterY = targetTop + targetHeight / 2;

    let snappedX: number | null = null;
    let snappedY: number | null = null;

    // Canvas center points
    const canvasCenterX = canvasWidth / 2;
    const canvasCenterY = canvasHeight / 2;

    // Collect alignment points from other objects
    const otherObjects = this.canvas.getObjects().filter(
      (obj) =>
        obj !== target &&
        (obj.type === 'textbox' || (obj.type === 'image' && !(obj as any).Background && !(obj as any).isMarker))
    );

    const alignmentPointsX: number[] = [canvasCenterX];
    const alignmentPointsY: number[] = [canvasCenterY];

    otherObjects.forEach((obj) => {
      const objBounds = obj.getBoundingRect();
      const objLeft = objBounds.left;
      const objRight = objBounds.left + objBounds.width;
      const objTop = objBounds.top;
      const objBottom = objBounds.top + objBounds.height;
      alignmentPointsX.push(objLeft, objLeft + objBounds.width / 2, objRight);
      alignmentPointsY.push(objTop, objTop + objBounds.height / 2, objBottom);
    });

    // Points to check on the target
    const targetXPoints = [
      { value: targetLeft, type: 'left' },
      { value: targetCenterX, type: 'centerX' },
      { value: targetRight, type: 'right' },
    ];
    const targetYPoints = [
      { value: targetTop, type: 'top' },
      { value: targetCenterY, type: 'centerY' },
      { value: targetBottom, type: 'bottom' },
    ];

    // Check horizontal alignments
    targetXPoints.forEach((point) => {
      alignmentPointsX.forEach((alignX) => {
        const distance = Math.abs(point.value - alignX);
        if (distance <= this.SNAP_THRESHOLD) {
          // Show guideline
          this.addAlignmentGuide(alignX, 0, alignX, canvasHeight);

          // Calculate snapped position
          if (point.type === 'left') {
            snappedX = alignX;
          } else if (point.type === 'centerX') {
            snappedX = alignX - targetWidth / 2;
          } else if (point.type === 'right') {
            snappedX = alignX - targetWidth;
          }
        }
      });
    });

    // Check vertical alignments
    targetYPoints.forEach((point) => {
      alignmentPointsY.forEach((alignY) => {
        const distance = Math.abs(point.value - alignY);
        if (distance <= this.SNAP_THRESHOLD) {
          // Show guideline
          this.addAlignmentGuide(0, alignY, canvasWidth, alignY);

          // Calculate snapped position
          if (point.type === 'top') {
            snappedY = alignY;
          } else if (point.type === 'centerY') {
            snappedY = alignY - targetHeight / 2;
          } else if (point.type === 'bottom') {
            snappedY = alignY - targetHeight;
          }
        }
      });
    });

    return { snappedX, snappedY };
  }

  //Saving JSON
  saveTemplate(): void {

    const updatedJSON = this.generateUpdatedTemplate();
    if (!updatedJSON) {
      console.error("Failed to generate JSON.");
      return;
    }

    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(updatedJSON, null, 2));
    const downloadAnchor = document.createElement("a");
    downloadAnchor.href = dataStr;
    downloadAnchor.download = "card-config.json";
    downloadAnchor.click();
  }
  validateTemplate(): string {
    if (this.newCardWithPhoto === 1 && (this.newCardUploaderLeft === undefined || this.newCardUploaderLeft === null)) {
      return "Please add placement marker for uploader element";
    }
    return "Ok";
  }
  handleObjectModified(event: any): void {
    const target = event.target as fabric.Object | undefined; // Correctly extract the target object
    this.clearAlignmentGuides();
    if (target && (target as any).isMarker === true) {
      this.newCardUploaderLeft = target.left ?? 0;
      this.newCardUploaderTop = target.top ?? 0;
      // console.log(`✅ Placement Marker Moved - Left: ${this.newCardUploaderLeft}, Top: ${this.newCardUploaderTop}`);
    }


  }

  // Clear alignment guides
  private clearAlignmentGuides(): void {
    if (!this.canvas) return;
    this.alignmentGuides.forEach((guide) => this.canvas!.remove(guide));
    this.alignmentGuides = [];
    this.canvas.renderAll();
  }

  // Add an alignment guide line
  private addAlignmentGuide(x1: number, y1: number, x2: number, y2: number): void {
    const guide = new fabric.Line([x1, y1, x2, y2], {
      stroke: this.GUIDE_COLOR,
      strokeWidth: this.GUIDE_STROKE_WIDTH,
      selectable: false,
      evented: false,
      excludeFromExport: true,
    });
    this.alignmentGuides.push(guide);
    this.canvas.add(guide);
  }


  generateUpdatedTemplate(): any {
    if (!this.canvas || !this.canvasConfig) {
      console.error("Canvas or canvasConfig is not initialized.");
      return;
    }

    const cCanvasWidth=this.canvasConfig.CanvasWidth??this.canvas.getWidth();
    const cCanvasHeight=this.canvasConfig.CanvasHeight??this.canvas.getHeight();

    const updatedJSON = {
      CardMain: {
        ProjectID: "123456",
        CanvasWidth: cCanvasWidth, //this.canvasConfig.CanvasWidth, //this.canvas.getWidth(), john 16.04.2025
        CanvasHeight:cCanvasHeight, //this.canvasConfig.CanvasHeight, //this.canvas.getHeight(), john 16.04.2025
        "imgsrc": this.canvasConfig["imgsrc"] ?? (this.combinePath(this.serverStoragePath, this.newCardBackground)),
        WithPhoto: this.canvasConfig["WithPhoto"] ?? this.newCardWithPhoto,
        hovertheme: this.canvasConfig["hovertheme"] ?? this.newCardHoverTheme,
        Elements: [] as Array<any>, // Flexible Elements array
      },
    };

    // Add uploader information
    if (this.canvasConfig.WithPhoto === 1) {
      const uploaderElement = this.canvasConfig.Elements.find(
        (e: any) => e.id === "image-uploader"
      );
      if (uploaderElement) {
        updatedJSON.CardMain.Elements.push({
          id: uploaderElement.id,
          name: uploaderElement.name,
          type: uploaderElement.type,
          properties: { ...uploaderElement.properties },
        });
      }
    }
    //Check if new card is being configured and if it has Photo, Then create the first time uploader element config
    if (this.newCardWithPhoto === 1) {
      const newElement = {
        "id": "image-uploader",
        "name": "image-uploader",
        "type": "image-uploader",
        "properties": {
          "showUploader": true,
          "uploaderWidth": 100,
          "uploaderHeight": 100,
          "uploaderLeft": this.newCardUploaderLeft,
          "uploaderTop": this.newCardUploaderTop,
          "left": this.newCardUploaderLeft,  // Value arrived from the selection of the location in picker on canvas.
          "top": this.newCardUploaderTop,    // Value arrived from the selection of the location in picker on canvas.
          "width": 357.14285714285717,
          "height": 500
        }

      };
      updatedJSON.CardMain.Elements.push(newElement);
    }



    // Process canvas objects
    this.canvas.getObjects().forEach((obj) => {
      const commonProperties = {
        left: obj.left,
        top: obj.top,
        scaleX: obj.scaleX,
        scaleY: obj.scaleY,
        angle: obj.angle,
        width: obj.width,
        height: obj.height,
        opacity: obj.opacity,
      };

      if (obj.type === "textbox" && obj instanceof fabric.Textbox) {
        updatedJSON.CardMain.Elements.push({
          type: "Textbox",
          properties: {
            ...commonProperties,
            text: obj.text,
            fontSize: obj.fontSize,
            fontFamily: obj.fontFamily,
            fill: obj.fill,
            textAlign: obj.textAlign,
            fontWeight: obj.fontWeight,
            fontStyle: obj.fontStyle,
            underline: obj.underline,
            lineHeight: obj.lineHeight,
          },
        });
      } else if (obj.type === "image" && obj instanceof fabric.Image) {

        const uploaderFlag = (obj as any).Uploader || 0;
        const Background = (obj as any).Background || 0;
        // Extract filename from stored property
        const src = (obj as any).src || null;
        // let base64 = obj.toDataURL(); // Generate a fresh base64 string for the image
        let base64;
        try {
          base64 = obj.toDataURL(); // Generate a fresh base64 string for the image
        }
        catch {

        }
        // if (Background === 1) {
        //   base64 = "";
        // }
        updatedJSON.CardMain.Elements.push({
          type: "image",
          properties: {
            ...commonProperties,
            Uploader: uploaderFlag,
            src: src || "",
            base64: base64 || "",
            Background: Background || "",
          },
        });
      } else {
        console.log("Unsupported object type:", obj.type);
      }
    });

    return updatedJSON;
  }


  combinePath(...parts: string[]): string {
    return parts
      .map((part) => part.replace(/(^\/+|\/+$)/g, '')) // Remove leading and trailing slashes
      .join('/'); // Combine parts with a single slash
  }


  hideImageUploaderImage(): void {
    const uploaderImageElement = document.getElementById('image-uploader-image');
    if (uploaderImageElement) {
      uploaderImageElement.style.display = 'none'; // Hide the element
      console.log('Image uploader element is now hidden.');
    } else {
      console.warn('Image uploader element with id "image-uploader-image" not found.');
    }
  }



  scaleCanvasToViewport(): void {
    if (!this.canvas) {
      //console.error("Canvas not initialized.");
      return;
    }

    // 🔹 Get full viewport height
    let viewportHeight = window.innerHeight;

    // 🔹 Define all possible header IDs that take up space
    this.totalHeaderHeight = 0; // Reset before calculation

    // 🔹 Loop through each header and sum up its height
    this.headerIds.forEach(id => {
      const element = document.getElementById(id);
      if (element) this.totalHeaderHeight += element.offsetHeight;
    });

    // 🔹 Reduce viewport height by total header height
    viewportHeight -= this.totalHeaderHeight;

    // 🔹 Maintain original aspect ratio
    const aspectRatio = this.canvasConfig.CanvasWidth / this.canvasConfig.CanvasHeight;

    // 🔹 Calculate new width & height based on adjusted viewport height
    const newWidth = viewportHeight * aspectRatio;
    const newHeight = viewportHeight;

    // 🔹 Calculate scale factor
    const scale = newHeight / this.canvasConfig.CanvasHeight;

    // console.log('Viewport Height (adjusted):', viewportHeight);
    // console.log('Total Header Height:', this.totalHeaderHeight);
    // console.log('Calculated Width:', newWidth);
    // console.log('Scale Factor:', scale);

    // 🔹 Apply zoom like Ctrl + Scroll behavior
    this.canvas.setZoom(scale);
    this.currentScale = scale;

    // 🔹 Update canvas dimensions
    this.canvas.setWidth(newWidth);
    this.canvas.setHeight(newHeight);

    // 🔹 Ensure Immediate Re-render
    this.canvas.renderAll();
  }

  getCanvasImage(): string {
    if (!this.canvas) {
      console.error('Canvas is not initialized.');
      return '';
    }

    return this.canvas.toDataURL({
      format: 'png',  // Specify the format
      multiplier: 1,   // Ensures original size is retained
    });
  }












}








